import { useState } from 'react';
import { m } from 'motion/react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { trackLeadSubmit, trackPhoneClick, trackEmailClick } from '../../lib/analytics';
import { submitLead, LEAD_DELIVERY_CONFIGURED } from '../../lib/leads';

export function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Anahtar ve gönderim mantığı lib/leads.ts içinde: sağdaki hızlı form da aynı
  // yerden geçiyor, ikinci bir alıcı kutusu eklemek orada tek satır.
  const CONTACT_EMAIL = 'info@meetingayrimenkul.com';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (LEAD_DELIVERY_CONFIGURED) {
      try {
        setIsSubmitting(true);
        await submitLead({
          formName: 'contact',
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
        });
        // Dönüşüm: form başarıyla gönderildi → GTM/GA4/Ads "generate_lead"
        trackLeadSubmit();
        setIsSubmitted(true);
        setTimeout(() => setIsSubmitted(false), 4000);
        setFormData({ firstName: '', lastName: '', phone: '', email: '', message: '' });
      } catch {
        setError('Form gönderilemedi. Lütfen daha sonra tekrar deneyin veya bizi arayın.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // mailto yedeği — lib/leads.ts içinde hiç anahtar tanımlı değilse çalışır
    const subject = encodeURIComponent('Rain Nova — Bilgi Talebi');
    const body = encodeURIComponent(
      `Ad: ${formData.firstName}\nSoyad: ${formData.lastName}\nTelefon: ${formData.phone}\nE-posta: ${formData.email}\n\nMesaj:\n${formData.message}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 4000);
    setFormData({ firstName: '', lastName: '', phone: '', email: '', message: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section id="contact" className="py-24 bg-white dark:bg-slate-950">
      <div className="container mx-auto px-4 lg:px-8">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full text-sm tracking-wider mb-4">
            İLETİŞİM
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-6">
            Hemen{' '}
            <span className="text-stone-700 dark:text-stone-300">
              İletişime Geçin
            </span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Rain Nova hakkında detaylı bilgi almak için formu doldurun, size en kısa sürede
            dönüş yapalım
          </p>
        </m.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <m.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot — hidden from users, bots will fill it */}
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                style={{ display: 'none' }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ad
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent transition-all"
                    placeholder="Adınız"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Soyad
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent transition-all"
                    placeholder="Soyadınız"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefon Numarası
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent transition-all"
                  placeholder="0 (5XX) XXX XX XX"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  E-posta
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent transition-all"
                  placeholder="ornek@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mesajınız (Opsiyonel)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent transition-all resize-none"
                  placeholder="Mesajınızı buraya yazın..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? 'Gönderiliyor...' : isSubmitted ? 'Gönderildi!' : 'Formu Gönder'}</span>
                <Send size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>

              {error && (
                <div className="text-center text-red-600 font-medium" role="alert">
                  {error}
                </div>
              )}

              {isSubmitted && (
                <m.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-green-600 font-medium"
                >
                  ✓ Formunuz başarıyla gönderildi. En kısa sürede dönüş yapacağız!
                </m.div>
              )}
            </form>
          </m.div>

          {/* Contact Info */}
          <m.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col justify-center"
          >
            <div className="bg-gradient-to-br from-slate-900 to-slate-900 text-white rounded-2xl p-8 space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-2">Rain Nova Satış Ofisi</h3>
                <p className="text-gray-300">
                  Hayalinizdeki eve kavuşmak için bugün bizi arayın
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-900 dark:bg-stone-700 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Telefon</p>
                    <p className="text-lg font-semibold">
                      <a href="tel:+905427609493" onClick={() => trackPhoneClick('+905427609493', 'contact_section')} className="hover:underline">
                        +90 (542) 760 94 93
                      </a>
                    </p>
                    <p className="text-lg font-semibold">
                      <a href="tel:+905016274044" onClick={() => trackPhoneClick('+905016274044', 'contact_section')} className="hover:underline">
                        +90 (501) 627 40 44
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-900 dark:bg-stone-700 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">E-posta</p>
                    <p className="text-lg font-semibold">
                      <a href="mailto:info@meetingayrimenkul.com" onClick={() => trackEmailClick('info@meetingayrimenkul.com', 'contact_section')} className="hover:underline">
                        info@meetingayrimenkul.com
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-900 dark:bg-stone-700 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Adres</p>
                    <p className="text-lg font-semibold">Esenkent, 34848 Maltepe / İstanbul</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Satış ofisimizi ziyaret etmek için randevu alın
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-700">
                <p className="text-sm text-gray-400">Çalışma Saatleri</p>
                <p className="text-white font-semibold">Pazartesi - Cumartesi: 09:00 - 19:00</p>
                <p className="text-white font-semibold">Pazar: 10:00 - 17:00</p>
              </div>
            </div>
          </m.div>
        </div>
      </div>
    </section>
  );
}
